import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import ArrowRight from 'components/Icons/ArrowRight'
import Clock from 'components/Icons/Clock'
import ClockAlert from 'components/Icons/ClockAlert'
import WebhookSetup from 'components/Icons/WebhookSetup'
import {
  INLINE_LINK_OVERRIDES,
  LINK_ARROW_GAP,
} from 'constants/inlineLinkButton'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { getRelativeTime } from 'utilities/date'
import type { OloWebhookConnectionState } from 'utilities/oloWebhookStatus'

const t = buildTranslate('live_updates.olo_webhook_banner')

const BANNER_PADDING = '16px'
const BANNER_RADIUS = '4px'
const BANNER_GAP = '16px'
const ICON_GAP = '8px'
const TEXT_GAP = '4px'

const RECENT_THRESHOLD_MS = 60 * 60 * 1000

export interface OloWebhookBannerProps {
  connectionState: OloWebhookConnectionState
  /** ISO timestamp of the most recent Olo webhook. Null/undefined when there
   * has never been one — only meaningful while `connectionState` is
   * 'connected', where it names the age in `connected_body`. */
  lastReceivedAt?: string | null
  /** Connected, but silent for over a day: swaps the banner to the yellow
   * "needs attention" treatment (title, body, icon, and colors) with a
   * `reconnect_link` CTA. */
  needsReconnect: boolean
  onManage: () => void
}

type BannerLink = {
  label: string
  onClick: () => void
}

type BannerContent = {
  icon: React.ReactNode
  title: string
  body: string
  link?: BannerLink
  /** Stale-for-24h (Figma 2733:23890): swaps the banner to a yellow warning
   * treatment so the page doesn't read as still healthy. */
  needsAttention?: boolean
}

// A timestamp is only "usable" for the age copy when it parses — a malformed
// value falls back to the no-timestamp copy rather than rendering "Invalid
// date" (mirrors the parsing guard in oloWebhookStatus.ts). Returns the value
// itself rather than a boolean so the caller keeps a narrowed `string` without
// asserting one.
const usableTimestamp = (
  lastReceivedAt: string | null | undefined
): string | null =>
  lastReceivedAt && Number.isFinite(new Date(lastReceivedAt).getTime())
    ? lastReceivedAt
    : null

// Webhook status syncs every 30 minutes, so a receipt under an hour old gets
// the vaguer "within the last hour" copy instead of a falsely-precise age.
const connectedBody = (receivedAt: string | null): string => {
  if (!receivedAt) return t('connected_body_no_timestamp')

  const receivedRecently =
    Date.now() - new Date(receivedAt).getTime() < RECENT_THRESHOLD_MS
  if (receivedRecently) return t('connected_body_recent')

  return t('connected_body', { age: getRelativeTime(receivedAt) })
}

// Content is a pure function of the three props that drive it, kept out of
// the component body so each state's shape is easy to compare at a glance
// rather than threaded through a growing ternary chain.
const deriveContent = (
  connectionState: OloWebhookConnectionState,
  lastReceivedAt: string | null | undefined,
  needsReconnect: boolean,
  onManage: () => void
): BannerContent => {
  switch (connectionState) {
    case 'not_connected':
      return {
        icon: <WebhookSetup />,
        title: t('not_connected_title'),
        body: t('not_connected_body'),
        link: { label: t('not_connected_link'), onClick: onManage },
      }
    case 'waiting':
      return {
        icon: <ClockAlert fillColor={palette.grey90} />,
        title: t('waiting_title'),
        body: t('waiting_body'),
        link: { label: t('waiting_link'), onClick: onManage },
      }
    case 'connected': {
      const receivedAt = usableTimestamp(lastReceivedAt)

      if (needsReconnect) {
        return {
          icon: <Clock fillColor={palette.grey90} />,
          title: t('needs_reconnect_title'),
          body: receivedAt
            ? t('needs_reconnect_body', { age: getRelativeTime(receivedAt) })
            : t('connected_body_no_timestamp'),
          link: { label: t('reconnect_link'), onClick: onManage },
          needsAttention: true,
        }
      }

      return {
        icon: <WebhookSetup />,
        title: t('connected_title'),
        body: connectedBody(receivedAt),
        // Deliberate: a healthy, non-stale connection gets no link at all —
        // the banner is a status readout, not a perpetual call to action.
        //
        // This resolves the question the old per-card panel parked as "OPEN /
        // DEVIATION" (7/27): that panel kept a "Manage webhook" link precisely
        // so a working connection still had a route to rotating its shared
        // secret. Product chose to match the design and drop it (8/12), so
        // there is now NO way to reopen the setup modal while the connection is
        // healthy — a merchant must wait for it to go quiet for 24h. Deliberate
        // trade-off, not an oversight; revisit here if secret rotation is asked
        // for.
        link: undefined,
      }
    }
  }
}

// Page-level Olo webhook connection banner (Figma 2732-18151): a single
// horizontal bar sitting above the status sections. Replaces the old
// per-card footer panel as the sole source of connection-health truth once a
// connection reaches 'connected' (see StatusCard/oloWebhookStatus).
const OloWebhookBanner = ({
  connectionState,
  lastReceivedAt,
  needsReconnect,
  onManage,
}: OloWebhookBannerProps) => {
  const [css] = useStyletron()

  const { icon, title, body, link, needsAttention } = deriveContent(
    connectionState,
    lastReceivedAt,
    needsReconnect,
    onManage
  )

  return (
    <div
      // Announces the connection health change (e.g. going stale) to screen
      // readers, matching the sibling GrantedRewardExpirationBanner pattern —
      // the yellow "needs attention" swap is otherwise sighted-only.
      role="status"
      data-testid="lu-olo-webhook-banner"
      className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: BANNER_GAP,
        padding: BANNER_PADDING,
        borderRadius: BANNER_RADIUS,
        border: `1px solid ${needsAttention ? palette.grey30 : palette.grey20}`,
        backgroundColor: needsAttention ? palette.eggYolk10 : palette.grey10,
      })}
    >
      {/* Zero flex-basis, not `auto`: a wrapping flex container assigns items to
          lines by their CONTENT width before any shrinking happens, so an auto
          basis pushes the CTA onto its own line instead of wrapping the copy.
          At 0 the cluster always shares the first line and grows into whatever
          the CTA leaves (Figma keeps the CTA pinned right). */}
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: TEXT_GAP,
          flex: '1 1 0%',
          minWidth: 0,
        })}
      >
        {/* The icon pairs with the title on its own row, leaving the body to
            span the full width beneath both (Figma 2733:23710) — centering it
            against the whole cluster instead leaves it floating beside the
            body's second line. */}
        <div
          data-testid="lu-olo-webhook-banner-title-row"
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: ICON_GAP,
          })}
        >
          <span
            aria-hidden="true"
            className={css({ display: 'flex', flexShrink: 0 })}
          >
            {icon}
          </span>

          {/* A real heading: this is the page's primary connection-health
              surface, so it belongs in the document outline rather than
              reading as loose bold text. */}
          <Text.BodyText4
            tag="h2"
            bold
            color="grey90"
            className={css({ display: 'block', margin: 0 })}
          >
            {title}
          </Text.BodyText4>
        </div>

        <Text.BodyText4 color="grey70" className={css({ display: 'block' })}>
          {body}
        </Text.BodyText4>
      </div>

      {link && (
        <Button
          kind="minimal"
          onClick={link.onClick}
          overrides={INLINE_LINK_OVERRIDES}
          className={css({ flexShrink: 0 })}
        >
          {link.label}
          {/* Decorative: the label already says what the link does, so the
              glyph stays out of the accessible name. From the icon library
              rather than a literal arrow — raw Unicode in JSX is not
              allowed. */}
          <span
            aria-hidden="true"
            className={css({
              display: 'inline-flex',
              alignItems: 'center',
              marginLeft: LINK_ARROW_GAP,
            })}
          >
            <ArrowRight />
          </span>
        </Button>
      )}
    </div>
  )
}

export default OloWebhookBanner
